import { type MenuOption } from '@saviynt/design-system';

import {
  ENABLE_DATA_TABLE,
  FIELD_TYPES,
  FORM_LAYOUT_CONFIG,
  getMaxColumns,
  VALIDATION_TYPES,
} from '../../../shared/constants';

import { type PaletteElement } from './types';

// Prompt's
export const FORM_SUBMIT_KIND = {
  SAVE: 'save',
  PUBLISH: 'publish',
} as const;

export const GO_BACK_PROMPT = 'go-back';
export const PREVIEW_PROMPT = 'preview';

export const FORM_BUILDER_PAGE_BANNERS = {
  // Form Validation
  FORM_VALIDATION_ERROR: 'form-validation-error',
  // Canvas drag and drop
  DROP_DENIED: 'drop-denied',
  DROP_DENIED_SECTION: 'drop-denied-section',
  DROP_DENIED_NOT_ON_CANVAS: 'drop-denied-not-on-canvas',
  FIELD_LIMIT_EXCEEDED: 'field-limit-exceeded',
  // Branding logo upload
  LOGO_UPLOAD_SUCCESS: 'logo-upload-success',
  LOGO_UPLOAD_TOO_LARGE: 'logo-upload-too-large',
  LOGO_UPLOAD_UNSUPPORTED_TYPE: 'logo-upload-unsupported-type',
  LOGO_UPLOAD_INFECTED: 'logo-upload-infected',
  LOGO_UPLOAD_FAILED: 'logo-upload-failed',
  LOGO_UPLOADING: 'logo-uploading',
  // Form save/publish errors
  FORM_SAVE_ERROR: 'form-save-error',
  FORM_PUBLISH_ERROR: 'form-publish-error',
} as const;

export const PALETTE_ELEMENTS_LABELS = {
  SECTION: 'Section',
  TEXT: 'Short Text',
  TEXTAREA: 'Long Text',
  SINGLESELECT: 'Single Select',
  MULTISELECT: 'Multi Select',
  DATATABLE: 'Data Table',
  CHECKBOX: 'Checkbox Cards',
  RADIO: 'Radio Cards',
  DATE: 'Date Picker',
  DATETIME: 'Date & Time',
  SPACER: 'Spacer',
  NUMBER: 'Number',
  BOOLEAN: 'Boolean',
  SENSITIVE: 'Sensitive',
  PASSWORD: 'Password',
  RICHTEXT: 'Rich Text',
  TNC: 'Terms and Conditions',
  CAPTCHA: 'Captcha',
};

export const FIELD_SOURCE_PALETTE = 'palette';

const BASE_PALETTE_ELEMENTS: PaletteElement[] = [
  {
    id: 'section',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.SECTION,
    label: PALETTE_ELEMENTS_LABELS.SECTION,
    colSpan: 0, // Sections are always full canvas width
  },
  {
    id: 'text',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.TEXT,
    label: PALETTE_ELEMENTS_LABELS.TEXT,
    colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  },
  {
    id: 'rich-text',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.RICHTEXT,
    label: PALETTE_ELEMENTS_LABELS.RICHTEXT,
    colSpan: getMaxColumns(), // Full-width field
  },
  {
    id: 'textarea',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.TEXTAREA,
    label: PALETTE_ELEMENTS_LABELS.TEXTAREA,
    colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  },
  {
    id: 'singleselect',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.SINGLESELECT,
    label: PALETTE_ELEMENTS_LABELS.SINGLESELECT,
    colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  },
  {
    id: 'multi-select',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.MULTISELECT,
    label: PALETTE_ELEMENTS_LABELS.MULTISELECT,
    colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  },
  {
    id: 'radio',
    fieldType: FIELD_TYPES.RADIO,
    source: FIELD_SOURCE_PALETTE,
    label: PALETTE_ELEMENTS_LABELS.RADIO,
    colSpan: getMaxColumns(), // Radios are always full canvas width
  },
  {
    id: 'checkbox',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.CHECKBOX,
    label: PALETTE_ELEMENTS_LABELS.CHECKBOX,
    colSpan: getMaxColumns(), // Checkboxes are always full canvas width
  },
  {
    id: 'date',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.DATE,
    label: PALETTE_ELEMENTS_LABELS.DATE,
    colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  },
  // TODO: Coming in future MVP
  // {
  //   id: 'date-time',
  //   source: FIELD_SOURCE_PALETTE,
  //   fieldType: FIELD_TYPES.DATETIME,
  //   label: PALETTE_ELEMENTS_LABELS.DATETIME,
  //   colSpan: 2,
  // },
  {
    id: 'spacer',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.SPACER,
    label: PALETTE_ELEMENTS_LABELS.SPACER,
    colSpan: getMaxColumns(), // Spacers are always full canvas width
  },
  {
    id: 'number',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.NUMBER,
    label: PALETTE_ELEMENTS_LABELS.NUMBER,
    colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  },
  {
    id: 'boolean',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.BOOLEAN,
    label: PALETTE_ELEMENTS_LABELS.BOOLEAN,
    colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  },
  // Will uncomment when RQ-24681 is fixed
  // {
  //   id: 'sensitive',
  //   source: FIELD_SOURCE_PALETTE,
  //   fieldType: FIELD_TYPES.SENSITIVE,
  //   label: PALETTE_ELEMENTS_LABELS.SENSITIVE,
  //   colSpan: FORM_LAYOUT_CONFIG.DEFAULT_COLSPAN,
  // },
  {
    id: 'password',
    source: FIELD_SOURCE_PALETTE,
    fieldType: FIELD_TYPES.PASSWORD,
    label: PALETTE_ELEMENTS_LABELS.PASSWORD,
    colSpan: 2, // Default to 2 since password confirmation is enabled by default
  },
  {
    id: 'terms-and-conditions',
    fieldType: FIELD_TYPES.TERMSANDCONDITIONS,
    source: FIELD_SOURCE_PALETTE,
    label: PALETTE_ELEMENTS_LABELS.TNC,
    colSpan: getMaxColumns(), // Full-width field
  },
];

// Conditionally include datatable element based on feature flag
export const PALETTE_ELEMENTS: PaletteElement[] = ENABLE_DATA_TABLE
  ? [
      ...BASE_PALETTE_ELEMENTS,
      {
        id: 'datatable',
        fieldType: FIELD_TYPES.DATATABLE,
        source: 'palette',
        label: PALETTE_ELEMENTS_LABELS.DATATABLE,
        colSpan: getMaxColumns(), // Full-width field
      },
    ]
  : BASE_PALETTE_ELEMENTS;

export const PALETTE_SMART_ELEMENTS_LABELS = {
  CAPTCHA: 'CAPTCHA',
  SIGNATURE: 'Signature',
  PHONENUMBER: 'Phone Number',
  ADDRESS: 'Address',
  FULLNAME: 'Full Name',
};

export const PALETTE_SMART_ELEMENTS: PaletteElement[] = [
  // {
  //   id: 'full-name',
  //   fieldType: FIELD_TYPES.FULLNAME,
  //   source: FIELD_SOURCE_PALETTE,
  //   label: PALETTE_SMART_ELEMENTS_LABELS.FULLNAME,
  //   colSpan: 2,
  // },
  // {
  //   id: 'address',
  //   fieldType: FIELD_TYPES.ADDRESS,
  //   source: FIELD_SOURCE_PALETTE,
  //   label: PALETTE_SMART_ELEMENTS_LABELS.ADDRESS,
  //   colSpan: 2,
  // },
  // {
  //   id: 'phone-number',
  //   fieldType: FIELD_TYPES.PHONENUMBER,
  //   source: FIELD_SOURCE_PALETTE,
  //   label: PALETTE_SMART_ELEMENTS_LABELS.PHONENUMBER,
  //   colSpan: 2,
  // },
  // {
  //   id: 'captcha',
  //   fieldType: FIELD_TYPES.CAPTCHA,
  //   source: FIELD_SOURCE_PALETTE,
  //   label: PALETTE_SMART_ELEMENTS_LABELS.CAPTCHA,
  //   colSpan: 2,
  // },
  // {
  //   id: 'signature',
  //   fieldType: FIELD_TYPES.SIGNATURE,
  //   source: FIELD_SOURCE_PALETTE,
  //   label: PALETTE_SMART_ELEMENTS_LABELS.SIGNATURE,
  //   colSpan: 2,
  // },
];

export const FIELDS = {
  text: 'text',
  checkbox: 'checkbox',
  textarea: 'textarea',
  dropdown: 'dropdown',
  multiselect: 'multiselect',
  number: 'number',
  dateTime: 'datetime',
  date: 'date',
  radio: 'radio',
};

export const ERROR_MESSAGE = 'Service unavailable. Please try after sometime.';

export const DATA_PROVIDER = {
  SQL: 'SQL',
  FIXED: 'FIXED',
  HTTP: 'HTTP',
  DATASTORE: 'DATASTORE',
} as const;

export const API_TIMEOUT_IN_MS = 10000; // ms;
export const FORMS_PER_PAGE = 10;
export const FIELD_LIMIT = {
  RICH_TEXT: 5,
  PASSWORD: 1,
  CAPTCHA: 1,
} as const;
/**
 * Maximum columns per row for NEW forms
 * Note: Existing forms may have colSpan values up to 4 (legacy support)
 */
export const MAX_COLUMNS = getMaxColumns();
export const MAX_RICH_TEXT_FIELDS = 5;

export const PATTERN_TYPE_ALPHABETIC = 'ALPHABETIC';
export const PATTERN_TYPE_ALPHANUMERIC = 'ALPHANUMERIC';
export const PATTERN_TYPE_EMAIL = 'EMAIL';
export const PATTERN_TYPE_PHONE = 'PHONE';
export const PATTERN_TYPE_CUSTOM = 'CUSTOM';

export const getPatternOptions = (l: (msg: string) => string): MenuOption[] => [
  { label: l('Alphabetic'), value: PATTERN_TYPE_ALPHABETIC },
  { label: l('Alphanumeric'), value: PATTERN_TYPE_ALPHANUMERIC },
  { label: l('Email'), value: PATTERN_TYPE_EMAIL },
  { label: l('Phone'), value: PATTERN_TYPE_PHONE },
  { label: l('Custom'), value: PATTERN_TYPE_CUSTOM },
];

export const PREDEFINED_REGEX_PATTERNS = {
  email: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
  letters: '^[a-zA-Z]+$',
  alphanumeric: '^[a-zA-Z0-9]+$',
  phone: '^[\\+]?[(]?[0-9]{3}[)]?[-\\s\\.]?[0-9]{3}[-\\s\\.]?[0-9]{4,6}$',
  custom: '',
} as const;

export const DEFAULT_PASSWORD_VALIDATION = [
  {
    message:
      'Password must be at least 12 characters long and include an uppercase letter, a lowercase letter, a number, a special character, and no spaces.',
    name: PATTERN_TYPE_CUSTOM,
    type: VALIDATION_TYPES.REGEX,
    value: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^A-Za-z0-9])\\S{12,}$',
  },
  {
    type: VALIDATION_TYPES.REQUIRED,
    value: '',
    message: 'This field is required',
  },
];

export const GROUP_FIELD_ORIENTAION = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
} as const;

export const initialPropertiesDrawerData = {
  isPropertiesOpen: false,
  selectedFieldData: null,
} as const;

export const initialPropmtData = {
  isPromptOpen: false,
  promptType: null,
  formSaveStatus: null,
  selectedFieldData: null,
} as const;

const URL_MAX_LENGTH = 100;

export const URL_VALIDATION = {
  REGEX: /^[a-z0-9-]+$/,
  MAX_LENGTH: URL_MAX_LENGTH,
  RESERVED_WORDS: [
    'admin',
    'api',
    'login',
    'create',
    'edit',
    'delete',
    'update',
    'logout',
    'signup',
    'register',
    'auth',
    'forgot-password',
    'reset-password',
    'verify-email',
    'dashboard',
    'settings',
    'config',
    'system',
    'graphql',
    'webhook',
    'internal',
    'assets',
    'static',
    'images',
    'js',
    'css',
    'fonts',
    'favicon.ico',
    'robots.txt',
    'sitemap.xml',
    'debug',
    'status',
    'health',
    'metrics',
    'docs',
    'swagger',
    'home',
    'about',
    'contact',
    'terms',
    'privacy',
    'help',
    'support',
    'search',
    'blog',
    'news',
  ],
  ERROR_MESSAGES: {
    MAX_LENGTH: `URL must be ${URL_MAX_LENGTH} characters or less`,
    INVALID_CHARACTERS:
      'Only lowercase letters (a-z), numbers (0-9), and hyphens (-) are allowed',
    SPACES_NOT_ALLOWED: 'Spaces are not allowed',
    RESERVED_WORD: 'This word is reserved and cannot be used',
    EMPTY: 'URL slug cannot be empty',
  },
} as const;

export const TOOL_ACCORDION_LABELS = {
  DETAILS: 'Details',
  ELEMENTS: 'Elements',
  BRANDING: 'Branding',
};

export const PROPERTIES_TAB_SELCTION = {
  PROPERTIES: '1',
  LOGIC: '2',
} as const;
